import React, { useEffect, useMemo, useRef, useState } from 'react';
import Header from '../../../components/maz-tu-info-bees/maz-tu-info-bees-admin/Header';

import styles from './styles.module.scss';
import * as XLSX from 'xlsx';
import fetchAPI from '../../../utils/fetchAPI';
import { Box, CircularProgress, Snackbar, Alert, AlertTitle } from '@mui/material';

import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-dt/css/dataTables.dataTables.min.css';

DataTable.use(DT);

export const StepGoals = () => {
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message1: '',
    message2: '',
    severity: 'info', // error | warning | success | info
  });

  const fileInputRef = useRef(null);
  const tableRef = useRef(null);

  // Helpers
  function esNumeroValido(valor) {
    return typeof valor === 'number' && !Number.isNaN(valor);
  }
  function esPocIdValido(valor) {
    return Number.isInteger(valor) && /^\d{9}$/.test(valor.toString());
  }
  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const showSnackbar = (message1, message2, severity = 'info') => {
    setSnackbar({
      open: true,
      message1,
      message2,
      severity,
    });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    resetFileInput();
    if (!file) return;
    setLoading(true);
    const reader = new FileReader();

    try {
      reader.onload = (e) => {
        const arrayBuffer = e.target.result;
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: null });

        const requiredColumns = ['poc_id', 'meta'];
        const fileColumns = Object.keys(jsonData[0] || {});

        const hasValidColumns =
          requiredColumns.every((col) => fileColumns.includes(col)) && fileColumns.length === requiredColumns.length;

        const hasInvalidPocId = jsonData.some((row) => !esPocIdValido(row.poc_id));

        const hasNonNumericValues = jsonData.some((row) =>
          requiredColumns.some((col) => esNumeroValido(row[col]) === false)
        );

        const hasEmptyValues = jsonData.some((row) =>
          requiredColumns.some((col) => row[col] === null || row[col] === undefined || row[col] === '')
        );

        if (!hasValidColumns) {
          setLoading(false);
          showSnackbar(
            'No se pudo cargar el archivo.',
            'Verificar que el archivo tenga exactamente las columnas requeridas: poc_id, meta,  e inténtalo nuevamente.',
            'error'
          );
          return;
        }

        if (hasEmptyValues) {
          setLoading(false);
          showSnackbar(
            'No se pudo cargar el archivo.',
            'Revisa que no haya celdas vacías en las columnas poc_id, meta e inténtalo nuevamente.',
            'error'
          );
          return;
        }

        if (hasNonNumericValues) {
          setLoading(false);
          showSnackbar(
            'El archivo no tiene valores válidos.',
            'Revisa que todas las celdas contengan solo números.',
            'error'
          );
          return;
        }

        if (hasInvalidPocId) {
          setLoading(false);
          showSnackbar(
            'El valor de poc_id no es válido.',
            'Debe ser un número de exactamente 9 dígitos, sin letras ni símbolos.',
            'error'
          );
          return;
        }

        const serviceUploadPointsExcel = 'v1/MX/admin/upload/goalexcel';
        fetchAPI({
          service: serviceUploadPointsExcel,
          protocol: 'POST',
          params: {
            info: jsonData,
          },
          callbackThen: (respond) => {
            setLoading(false);

            if (!respond || respond.error || !respond?.data) {
              showSnackbar('Error al procesar el archivo.', '', 'error');
              return;
            }

            showSnackbar('¡Carga realizada exitosamente!', '', 'success');
            tableRef.current?.dt()?.ajax?.reload();
          },
          callbackCatch: () => {
            setLoading(false);
            showSnackbar('Error de comunicación con el servidor.', '', 'error');
          },
        });
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      setLoading(false);
      showSnackbar('Error al procesar el archivo.', '', 'error');
      console.error(error);
    }
  };

  const tableOptions = useMemo(() => ({
    serverSide: true,
    processing: false,
    ordering: false,
    paging: true,
    searching: true,
    lengthChange: true,
    pageLength: 10,
    language: {
      url: '//cdn.datatables.net/plug-ins/1.13.8/i18n/es-ES.json',
    },

    ajax: (dtParams, callback) => {
      const page = dtParams.start / dtParams.length + 1;
      const limit = dtParams.length;
      const search = dtParams.search.value;
      setLoading(true);

      fetchAPI({
        service: `v1/MX/admin/upload/goals?page=${page}&limit=${limit}&search=${search}`,
        protocol: 'GET',
        callbackThen: (res) => {
          callback({
            data: res.data,
            recordsTotal: res.pagination.totalItems,
            recordsFiltered: res.pagination.totalFiltered,
          });
          setLoading(false);
        },
        callbackCatch: () => {
          callback({
            data: [],
            recordsTotal: 0,
            recordsFiltered: 0,
          });
          setLoading(false);
        },
      });
    },

    columns: [
      {
        data: 'customer_no',
        title: 'Customer',
        className: 'dt-left',
      },
      {
        data: 'meta',
        title: 'Meta',
        className: 'dt-center',
      },
      {
        data: 'created_at',
        title: 'Fecha de creación',
        className: 'dt-center',
        render: (data) => (data ? new Date(data).toLocaleDateString('es-ES') : ''),
      },
      {
        data: 'updated_at',
        title: 'Fecha de actualización',
        className: 'dt-center',
        render: (data) => (data ? new Date(data).toLocaleDateString('es-ES') : ''),
      },
    ],
  }));

  return (
    <div className={styles.container}>
      <Header title="Carga Escalón Metas" userName="XXX" onClickBack={() => setShowForm(false)} back={showForm} />

      <div className={styles.body}>
        <section>
          <button className={`${styles.btn} ${styles['btn--black']}`} onClick={() => fileInputRef.current.click()}>
            Cargar archivo de metas
          </button>

          <input
            type="file"
            accept=".xlsx, .xls, .csv"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
            data-testid="file-input"
          />
        </section>

        {loading && (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '50vh',
              }}
            >
              <CircularProgress />
            </Box>
          </>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
            px: 5,
            pb: 5,
          }}
        >
          <div style={{ width: '100%', display: loading ? 'none' : 'block' }}>
            <DataTable ref={tableRef} className="display" options={tableOptions} />
          </div>
        </Box>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message1 && <AlertTitle>{snackbar.message1}</AlertTitle>}
          {snackbar.message2}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default StepGoals;
